import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { ModalsZIndex } from 'src/components/constants';
import { LggOptionsDropdownButtonWithCustomTriggerControlled } from 'src/components/general/button/dropdown-button';
import { StyledButtonV2 } from 'src/components/general/button/lgg-button';
import {
  DrawerOption,
  OptionsBottomDrawer,
} from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';

export const PlaceholderNode = styled.span`
  background: ${({ theme }) => theme.colors.secondaryGold10};
  border-radius: 4px;
  box-shadow: ${({ theme }) => theme.colors.secondaryGold40} 0px 0px 0px 1px inset;
  color: ${({ theme }) => theme.colors.secondaryGoldDark};
  cursor: pointer;
  display: inline-block;
  font-size: 13px;
  letter-spacing: -0.36px;
  line-height: 14px;
  padding: 2px 4px;
  user-select: text;

  &.active,
  &:hover,
  &:focus,
  &:active {
    box-shadow: ${({ theme }) => theme.colors.secondaryGold80} 0px 0px 0px 2px inset;
  }

  &.error {
    box-shadow: ${({ theme }) => theme.colors.secondaryCoral60} 0px 0px 0px 1px inset;
  }
`;

export const TemplatePlaceholderOverlayContainer = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  border-radius: 6px;
  box-shadow: 0px 20px 40px 0px rgba(91, 101, 112, 0.1);
  width: 100%;

  ${up('md')} {
    width: 299px;
  }
`;

const OverlayTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  letter-spacing: 0px;
  line-height: 18px;
`;

const OverlaySubTitle = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  letter-spacing: 0px;
  line-height: 16px;
`;

export const TemplatePlaceholderOverlayHeaderContainer = styled(FlexColumn)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  padding: 20px;

  ${up('md')} {
    padding: 15px;
  }
`;

type TemplatePlaceholderOverlayHeaderProps = {
  title: string;
  subTitle?: string;
};

export const TemplatePlaceholderOverlayHeader = ({
  title,
  subTitle,
}: TemplatePlaceholderOverlayHeaderProps) => {
  return (
    <TemplatePlaceholderOverlayHeaderContainer>
      <OverlayTitle>{title}</OverlayTitle>
      {subTitle ? <OverlaySubTitle>{subTitle}</OverlaySubTitle> : null}
    </TemplatePlaceholderOverlayHeaderContainer>
  );
};

export const TemplatePlaceholderOverlayBody = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  padding: 20px 20px 60px;

  ${up('md')} {
    padding: 10px;
  }
`;

const FooterButton = styled(StyledButtonV2)`
  height: 38px;
  font-size: 14px;
  line-height: 18px;

  ${up('md')} {
    font-size: 12px;
    height: 30px;
  }
`;

export const TemplatePlaceholderOverlayFooterContainer = styled(FlexRow)`
  padding: 15px 20px;

  ${up('md')} {
    padding: 10px;
  }

  ${FooterButton} {
    flex: 1;
  }

  ${FooterButton} + ${FooterButton} {
    margin-left: 20px;

    ${up('md')} {
      margin-left: 10px;
    }
  }
`;

const RemoveOptionIcon = styled(Icon)`
  svg {
    path {
      fill: ${({ theme }) => theme.colors.secondaryCoral};
    }
  }
`;

type TemplatePlaceholderOverlayFooterProps = {
  onClose: VoidFunction;
  submitTextOverride?: string;
};

export const TemplatePlaceholderOverlayFooter = ({
  onClose,
  submitTextOverride,
}: TemplatePlaceholderOverlayFooterProps) => {
  const { t } = useTranslation(['common']);

  return (
    <TemplatePlaceholderOverlayFooterContainer>
      <FooterButton
        variant="default"
        type="button"
        size="small"
        data-lgg-id="close-placeholder-overlay-form"
        onClick={() => {
          onClose();
        }}
      >
        {t('common:cancel')}
      </FooterButton>
      <FooterButton
        type="submit"
        variant="primary"
        size="small"
        data-lgg-id="submit-placeholder-overlay-form"
      >
        {submitTextOverride ?? t('common:insert')}
      </FooterButton>
    </TemplatePlaceholderOverlayFooterContainer>
  );
};

type PlaceholderOptionsOverlayProps = {
  children: React.ReactChild;
  onEdit: VoidFunction;
  onRemove: VoidFunction;
};

export const PlaceholderOptionsOverlay = ({
  children,
  onEdit,
  onRemove,
}: PlaceholderOptionsOverlayProps) => {
  const { t } = useTranslation(['common', 'conversations']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const moreOptionsVisibilityHandler = useVisible();

  const options: DrawerOption[] = useMemo(
    () => [
      {
        label: t('common:edit'),
        icon: 'edit',
        onClick: onEdit,
        'data-lgg-id': 'edit-placeholder-overlay-option',
      },
      {
        label: t('common:remove'),
        icon: <RemoveOptionIcon type="deleteBin" />,
        onClick: onRemove,
        'data-lgg-id': 'remove-placeholder-overlay-option',
      },
    ],
    [onEdit, onRemove, t],
  );

  return breakpointUpMd ? (
    <LggOptionsDropdownButtonWithCustomTriggerControlled
      customTrigger={children}
      options={options}
    />
  ) : (
    <>
      <span onClick={moreOptionsVisibilityHandler.show}>{children}</span>
      <OptionsBottomDrawer
        visible={moreOptionsVisibilityHandler.visible}
        zIndex={ModalsZIndex}
        title={t(
          'conversations:messageInput.options.messageTemplates.placeholderOptions',
        )}
        onClose={moreOptionsVisibilityHandler.close}
        options={options}
      />
    </>
  );
};
